import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import { OrderCardBubbles } from '../components/OrderCardBubbles';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './MulliganDialog.css';

/**
 * Mulligan Dialog Component
 * Displays initial 3 orders at game start
 * Allows players to toggle orders for replacement
 * Provides "Replace Selected" or "Keep all" button
 * 
 * Mobile support: When isMobile is true, uses a compact flex layout (no vertical offset)
 * so three cards stay fully visible in landscape.
 */
const MulliganDialog = ({ orders, onSubmitMulligan, playerId, isMobile = false, playerGoesFirst }) => {
  const { t } = useTranslation();
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const overlayRef = useRef(null);
  const orderIdentityKey = useMemo(
    () => (orders || []).map((order) => order?.instanceId).filter(Boolean).join('\0'),
    [orders]
  );

  // Reset selection only when the mulligan card set changes — not on every new orders[] reference.
  useEffect(() => {
    setSelectedOrders(new Set());
  }, [orderIdentityKey]);

  // Mobile: block wheel / touch-scroll chaining (overflow is also hidden in CSS)
  useEffect(() => {
    if (!isMobile) return undefined;
    const el = overlayRef.current;
    if (!el) return undefined;
    const blockScroll = (e) => {
      e.preventDefault();
    };
    el.addEventListener('wheel', blockScroll, { passive: false });
    el.addEventListener('touchmove', blockScroll, { passive: false });
    return () => {
      el.removeEventListener('wheel', blockScroll);
      el.removeEventListener('touchmove', blockScroll);
    };
  }, [isMobile, orders]);

  const handleOrderClick = (order) => {
    if (!order || !order.instanceId) return;
    
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(order.instanceId)) {
      // Disabling order (removing from selected)
      playSoundEffect('/sounds/mulligan_enable.mp3');
      newSelected.delete(order.instanceId);
    } else {
      // Enabling order (adding to selected)
      playSoundEffect('/sounds/mulligan_remove.mp3');
      newSelected.add(order.instanceId);
    }
    setSelectedOrders(newSelected);
  };

  const handleSubmit = () => {
    playSoundEffect('/sounds/click.mp3');
    if (onSubmitMulligan) {
      onSubmitMulligan(Array.from(selectedOrders));
    }
  };

  // Early return if no orders
  if (!orders || orders.length === 0) {
    return null;
  }

  if (playerGoesFirst == null) {
    throw new Error('MulliganDialog: playerGoesFirst is required when displaying mulligan orders');
  }

  const turnOrderHint = playerGoesFirst
    ? t('battleUI.mulliganGoFirst')
    : t('battleUI.mulliganGoSecond');

  const numSelected = selectedOrders.size;
  const buttonText = numSelected > 0 
    ? t('battleUI.replaceSelected') 
    : t('battleUI.keepAll');

  return (
    <div
      ref={overlayRef}
      className={`mulligan-overlay${isMobile ? ' mulligan-overlay--mobile' : ''}`}
      data-no-battle-flashlight="true"
    >
      <div className={`mulligan-dialog${isMobile ? ' mulligan-mobile' : ''}`}>
        <div className="mulligan-header">
          <p>{t('battleUI.mulliganDescription')} {turnOrderHint}</p>
        </div>

        <div className="mulligan-orders-display">
          {orders.map((order, index) => {
            if (!order) return null;
            const isSelected = selectedOrders.has(order.instanceId);
            return (
              <div
                key={order.instanceId || index}
                className="mulligan-order-card"
                data-mulligan-order-id={order.id}
                onClick={() => handleOrderClick(order)}
                onMouseEnter={() => playSoundEffect('/sounds/hoverover.mp3', 0.3)}
              >
                <div 
                  className={isSelected ? 'mulligan-order-selected' : ''}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <OrderDesign
                    order={order}
                    count={0}
                    minimal={false}
                    isOOR={false}
                    isSelected={false}
                    isTarget={false}
                    isMulligan={true}
                  />
                </div>
                {isSelected && <div className="mulligan-order-overlay" />}
                {!isSelected && <OrderCardBubbles rarity={order.rarity || 'common'} />}
              </div>
            );
          })}
        </div>

        <div className="mulligan-action-buttons">
          <button
            className="mulligan-submit-button"
            onClick={handleSubmit}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MulliganDialog;

